import {
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "./navigation-menu";

export type RichNavLinkItem = {
  title: string;
  href: string;
  description: string;
  icon: LucideIcon;
};

export type RichNavGroup = {
  title: string;
  items: RichNavLinkItem[];
};

export type RichNavMenuItem =
  | {
      type: "split";
      label: string;
      icon: LucideIcon;
      mainTitle: string;
      mainItems: RichNavLinkItem[];
      sideTitle: string;
      sideItems: RichNavLinkItem[];
      active?: boolean;
    }
  | {
      type: "grid";
      label: string;
      icon: LucideIcon;
      sectionTitle: string;
      items: RichNavLinkItem[];
      active?: boolean;
    }
  | {
      type: "groups";
      label: string;
      icon: LucideIcon;
      groups: RichNavGroup[];
      active?: boolean;
    }
  | {
      type: "link";
      label: string;
      href: string;
      icon: LucideIcon;
      active?: boolean;
    };

const headerTriggerClass = cn(
  navigationMenuTriggerStyle(),
  "h-8 gap-1.5 whitespace-nowrap bg-transparent px-3 text-sm font-medium text-white/95 shadow-none transition-all duration-200",
  "hover:-translate-y-0.5 hover:bg-white/10 hover:text-white hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_6px_14px_rgba(0,0,0,0.18)]",
  "focus:bg-white/10 focus:text-white",
  "data-[active]:-translate-y-0.5 data-[active]:bg-white data-[active]:text-primary data-[active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_18px_rgba(0,0,0,0.22)]",
  "data-[state=open]:-translate-y-0.5 data-[state=open]:bg-white data-[state=open]:text-primary data-[state=open]:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_18px_rgba(0,0,0,0.22)]",
  "xl:px-4"
);

function TriggerLabel({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}) {
  return (
    <>
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      <span>{label}</span>
    </>
  );
}

function isPathActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function itemsMatchPath(
  pathname: string,
  items: { href: string }[]
): boolean {
  return items.some((item) => isPathActive(pathname, item.href));
}

type RichNavigationMenuProps = {
  items: RichNavMenuItem[];
};

export default function RichNavigationMenu({ items }: RichNavigationMenuProps) {
  if (items.length === 0) return null;

  return (
    <NavigationMenu className="z-20" triggerMode="click">
      <NavigationMenuList>
        {items.map((item) => {
          if (item.type === "link") {
            return (
              <NavigationMenuItem key={item.href}>
                <NavigationMenuLink asChild active={item.active}>
                  <Link to={item.href} className={headerTriggerClass}>
                    <TriggerLabel icon={item.icon} label={item.label} />
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            );
          }

          if (item.type === "split") {
            const hasSide = item.sideItems.length > 0;
            return (
              <NavigationMenuItem key={item.label}>
                <NavigationMenuTrigger
                  className={headerTriggerClass}
                  data-active={item.active ? "true" : undefined}
                >
                  <TriggerLabel icon={item.icon} label={item.label} />
                </NavigationMenuTrigger>
                <NavigationMenuContent className="px-0 py-1">
                  <div
                    className={cn(
                      "grid gap-3 p-4",
                      hasSide
                        ? "w-[min(56.25rem,calc(100vw-16rem))] grid-cols-1 divide-neutral-200 md:grid-cols-3 md:divide-x"
                        : "w-[min(36rem,calc(100vw-16rem))] grid-cols-1"
                    )}
                  >
                    <div className={hasSide ? "col-span-1 pe-2 md:col-span-2" : ""}>
                      <h6 className="pl-2.5 font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                        {item.mainTitle}
                      </h6>
                      <ul className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {item.mainItems.map((entry) => (
                          <ListItem
                            href={entry.href}
                            icon={entry.icon}
                            key={entry.href}
                            title={entry.title}
                          >
                            {entry.description}
                          </ListItem>
                        ))}
                      </ul>
                    </div>

                    {hasSide ? (
                      <div className="pl-0 md:pl-4">
                        <h6 className="pl-2.5 font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                          {item.sideTitle}
                        </h6>
                        <ul className="mt-2.5 grid gap-2">
                          {item.sideItems.map((entry) => (
                            <ListItem
                              href={entry.href}
                              icon={entry.icon}
                              key={entry.href}
                              title={entry.title}
                            >
                              {entry.description}
                            </ListItem>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            );
          }

          if (item.type === "grid") {
            return (
              <NavigationMenuItem key={item.label}>
                <NavigationMenuTrigger
                  className={headerTriggerClass}
                  data-active={item.active ? "true" : undefined}
                >
                  <TriggerLabel icon={item.icon} label={item.label} />
                </NavigationMenuTrigger>
                <NavigationMenuContent className="p-4">
                  <h6 className="pl-2.5 font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                    {item.sectionTitle}
                  </h6>
                  <ul className="mt-2.5 grid w-[min(25rem,calc(100vw-16rem))] gap-2 md:w-[min(31.25rem,calc(100vw-16rem))] md:grid-cols-2 lg:w-[min(37.5rem,calc(100vw-16rem))]">
                    {item.items.map((entry) => (
                      <ListItem
                        href={entry.href}
                        icon={entry.icon}
                        key={entry.href}
                        title={entry.title}
                      >
                        {entry.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            );
          }

          return (
            <NavigationMenuItem key={item.label}>
              <NavigationMenuTrigger
                className={headerTriggerClass}
                data-active={item.active ? "true" : undefined}
              >
                <TriggerLabel icon={item.icon} label={item.label} />
              </NavigationMenuTrigger>
              <NavigationMenuContent className="p-4">
                <div className="grid w-[min(56.25rem,calc(100vw-16rem))] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {item.groups.map((group) => (
                    <div key={group.title}>
                      <h6 className="pl-2.5 font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                        {group.title}
                      </h6>
                      <ul className="mt-2.5 grid gap-1">
                        {group.items.map((entry) => (
                          <ListItem
                            compact
                            href={entry.href}
                            icon={entry.icon}
                            key={entry.href}
                            title={entry.title}
                          >
                            {entry.description}
                          </ListItem>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

const ListItem = React.forwardRef<
  HTMLAnchorElement,
  Omit<React.ComponentPropsWithoutRef<typeof Link>, "to"> & {
    icon: LucideIcon;
    href: string;
    title: string;
    compact?: boolean;
  }
>(({ className, title, children, icon: Icon, href, compact, ...props }, ref) => {
  const description = typeof children === "string" ? children : "";

  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          className={cn(
            "block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            compact && "p-2",
            className
          )}
          ref={ref}
          to={href}
          {...props}
        >
          <div className="flex items-center gap-2 font-semibold leading-none tracking-tight text-neutral-900">
            <Icon className={cn("h-5 w-5 shrink-0 text-primary", compact && "h-4 w-4")} />
            {title}
          </div>
          {!compact && description ? (
            <p className="mt-2 line-clamp-2 text-muted-foreground text-sm leading-snug">
              {description}
            </p>
          ) : null}
        </Link>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";
